/**
 * SageComposer — message input for the Sage chat.
 *
 * Visually mirrors the Team Chat composer in ChatFABPanel, but strips the
 * markdown formatting toolbar (Bold/Italic/Link/etc.), emoji, @ mention, and
 * file attachments — none of it matters when talking to a bot. The reclaimed
 * action-bar space holds two voice controls:
 *
 *  1. Voice note (mic): tap to start recording, tap to stop & send. Transcript
 *     auto-posts via /api/v1/sage/messages.
 *  2. Conversation mode (radio/broadcast): enters a hands-free loop where the
 *     mic stays active, silence → auto-send, Sage's reply auto-plays, then
 *     the mic re-opens. Controlled by onConversationToggle — the FAB-level
 *     UI is responsible for the immersive overlay.
 *
 * Sits inside the existing SageChat layout.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Mic, Radio, Send, Square, Trash2, Loader2 } from 'lucide-react'
import { authedFetch, getToken } from '@/lib/api';
import { tokens } from '@/design-system/tokens'

const SAGE_SESSION_KEY = 'sage_session_id'
const MIN_RECORDING_MS = 350

/** Imperative handle — lets the parent prefill / focus the draft without
 *  lifting the draft state back up (SAGE-SPEED-4). */
export interface SageComposerHandle {
  setText: (text: string) => void
  focus: () => void
}

interface SageComposerProps {
  /** Fires when the user hits Send / Enter, with the trimmed draft text.
   *  Return a promise so we can disable the button while the parent
   *  dispatches. The composer owns + clears its own draft. */
  onSend: (text: string) => Promise<void> | void
  /** Disables the textarea + primary send (e.g. waiting on a response). */
  loading?: boolean
  /** Fires after voice-note transcription with the resulting text so the
   *  parent can forward it through its own sendMessage path. */
  onVoiceNoteTranscript?: (text: string) => void
  /** Called when user activates conversation mode. The parent takes over
   *  UI (FAB transforms, etc.). */
  onConversationStart?: () => void
  /** Whether conversation mode is currently active — used to highlight the
   *  button. */
  conversationActive?: boolean
  /** Placeholder for the textarea. */
  placeholder?: string
  /** Brand color used on the Send button + conversation-active state. */
  brandColor?: string
}

type MicState = 'idle' | 'recording' | 'transcribing' | 'error'

export const SageComposer = forwardRef<SageComposerHandle, SageComposerProps>(function SageComposer({
  onSend, loading = false,
  onVoiceNoteTranscript, onConversationStart, conversationActive = false,
  placeholder = 'Ask Sage…',
  brandColor = 'var(--brand-gold)',
}, ref) {
  // Draft lives here (not lifted to SageEmbed) so typing re-renders only the
  // composer, never the message list — the SAGE-SPEED-4 keystroke-latency fix.
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useImperativeHandle(ref, () => ({
    setText: (text: string) => {
      setValue(text)
      requestAnimationFrame(() => textareaRef.current?.focus())
    },
    focus: () => textareaRef.current?.focus(),
  }), [])

  // Trim, clear the draft, reset autogrow, and hand the content to the parent.
  const submit = useCallback(() => {
    const text = value.trim()
    if (!text || loading) return
    setValue('')
    const ta = textareaRef.current
    if (ta) ta.style.height = 'auto'
    void onSend(text)
  }, [value, loading, onSend])
  const [micState, setMicState] = useState<MicState>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [micError, setMicError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const recordStartRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const cancelledRef = useRef(false)

  // ── Autogrow textarea ──────────────────────────────────────────────────
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = Math.min(120, ta.scrollHeight) + 'px'
  }, [value])

  // ── Cleanup on unmount ─────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current); timerRef.current = null
    }
    mediaRecorderRef.current = null
    chunksRef.current = []
    cancelledRef.current = false
    setElapsed(0)
  }, [])
  useEffect(() => () => cleanup(), [cleanup])

  // ── Voice note recording ───────────────────────────────────────────────
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError('Mic not supported'); setMicState('error')
      setTimeout(() => { setMicState('idle'); setMicError(null) }, 2500); return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      })
      mediaRecorderRef.current = mr
      chunksRef.current = []
      mr.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        const dur = Date.now() - recordStartRef.current
        const blob = new Blob(chunksRef.current, { type: mr.mimeType })
        streamRef.current?.getTracks().forEach(t => t.stop())
        streamRef.current = null
        if (timerRef.current !== null) { window.clearInterval(timerRef.current); timerRef.current = null }
        if (cancelledRef.current) { cancelledRef.current = false; setMicState('idle'); setElapsed(0); return }
        if (dur < MIN_RECORDING_MS) {
          setMicError('Too short'); setMicState('error'); setElapsed(0)
          setTimeout(() => { setMicState('idle'); setMicError(null) }, 1500); return
        }
        void transcribeAndSend(blob)
      }
      mr.start()
      recordStartRef.current = Date.now()
      setMicState('recording')
      timerRef.current = window.setInterval(() => {
        setElapsed(Math.floor((Date.now() - recordStartRef.current) / 1000))
      }, 200)
    } catch (err) {
      setMicError(err instanceof Error && err.name === 'NotAllowedError'
        ? 'Mic permission denied' : 'Mic error')
      setMicState('error')
      setTimeout(() => { setMicState('idle'); setMicError(null) }, 3000)
    }
  }

  const stopAndSend = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop()
  }
  const cancelRecording = () => {
    cancelledRef.current = true
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop()
  }

  const transcribeAndSend = async (blob: Blob) => {
    setMicState('transcribing')
    const fd = new FormData()
    fd.append('audio', blob, 'sage.webm')
    try {
      const res = await authedFetch('/api/v1/sage/transcribe', {
        method: 'POST',
        body: fd,
      }, { redirectOnAuthFailure: false })
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(body.error || `transcribe ${res.status}`)
      }
      const data = await res.json() as { text: string }
      const text = (data.text || '').trim()
      if (!text) throw new Error('No speech detected')
      // Parent decides how to send (dispatch through SageChat's sendMessage).
      if (onVoiceNoteTranscript) {
        onVoiceNoteTranscript(text)
      } else {
        // Fallback: post directly to /sage/messages using the persisted
        // session_id so voice notes still work if the parent didn't wire it.
        const sid = localStorage.getItem(SAGE_SESSION_KEY) || undefined
        await authedFetch('/api/v1/sage/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: text, session_id: sid }),
        }, { redirectOnAuthFailure: false })
      }
      setMicState('idle')
      setElapsed(0)
    } catch (e) {
      setMicError(e instanceof Error ? e.message : 'Transcribe failed')
      setMicState('error')
      setElapsed(0)
      setTimeout(() => { setMicState('idle'); setMicError(null) }, 3000)
    }
  }

  // ── Keyboard ────────────────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      submit()
    }
  }

  const fmtSec = (s: number) => {
    const m = Math.floor(s / 60), r = s % 60
    return `${m}:${r.toString().padStart(2, '0')}`
  }

  const isRecording = micState === 'recording'
  const isTranscribing = micState === 'transcribing'

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Row 1: textarea (replaced by recording status bar when recording) */}
      {isRecording ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px',
          background: tokens.color.feedback.error + '14',
          fontFamily: tokens.typography.fontFamily.sans,
          fontSize: 13,
          color: tokens.color.text.primary,
        }}>
          <span style={{
            display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
            background: tokens.color.feedback.error, animation: 'sage-comp-pulse 1.2s infinite',
          }} />
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtSec(elapsed)}</span>
          <span style={{ color: tokens.color.text.muted, fontSize: 12 }}>Recording — tap trash to cancel</span>
          <div style={{ flex: 1 }} />
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isTranscribing ? 'Transcribing…' : placeholder}
          disabled={loading || isTranscribing}
          rows={1}
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box',
            border: 'none', outline: 'none', resize: 'none', overflow: 'hidden',
            padding: '10px 12px', fontSize: 14,
            fontFamily: tokens.typography.fontFamily.sans,
            background: 'transparent', color: 'var(--text-primary)',
            lineHeight: '1.4', maxHeight: 120,
          }}
        />
      )}

      {/* Row 2: action bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 8px 6px',
        borderTop: '1px solid var(--border)',
      }}>
        {/* Voice note (mic) — tap to start, tap again to send */}
        <button
          className="sage-comp-btn"
          onClick={isRecording ? stopAndSend : startRecording}
          disabled={isTranscribing || conversationActive}
          aria-label={isRecording ? 'Stop and send voice note' : 'Record voice note'}
          style={{
            background: isRecording ? tokens.color.feedback.error : 'transparent',
            color: isRecording ? 'var(--bg-page)' : tokens.color.text.muted,
            border: 'none',
            borderRadius: 6,
            width: 30, height: 30,
            cursor: (isTranscribing || conversationActive) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          {isTranscribing
            ? <Loader2 size={15} style={{ animation: 'sage-comp-spin 0.8s linear infinite' }} />
            : <Mic size={15} strokeWidth={2} />}
        </button>

        {/* Cancel recording — only while recording */}
        {isRecording && (
          <button
            className="sage-comp-btn"
            onClick={cancelRecording}
            aria-label="Cancel recording"
            style={{
              background: 'transparent',
              color: tokens.color.feedback.error,
              border: 'none',
              borderRadius: 6,
              width: 30, height: 30,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Trash2 size={15} strokeWidth={2} />
          </button>
        )}

        {/* Conversation mode — hands-free loop */}
        {!isRecording && (
          <button
            className="sage-comp-btn"
            onClick={() => onConversationStart?.()}
            disabled={isTranscribing}
            aria-label={conversationActive ? 'Conversation mode is active' : 'Start voice conversation mode'}
            style={{
              background: conversationActive ? brandColor : 'transparent',
              color: conversationActive ? 'var(--brand-gold-text)' : tokens.color.text.muted,
              border: 'none',
              borderRadius: 6,
              width: 30, height: 30,
              cursor: isTranscribing ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
          >
            <Radio size={15} strokeWidth={2} />
          </button>
        )}

        {/* Error toast */}
        {micError && (
          <span style={{
            fontSize: 11,
            color: tokens.color.feedback.error,
            fontFamily: tokens.typography.fontFamily.sans,
            marginLeft: 4,
          }}>{micError}</span>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Send */}
        {/* Send button — Sage's identity is fluoro gold, so this stays on
            the brand token even if the company has a custom primary_color.
            On light mode var(--brand-gold) resolves to #E8B84B (amber) so
            dark text remains legible. */}
        <button
          onClick={submit}
          disabled={loading || isTranscribing || !value.trim()}
          aria-label="Send"
          style={{
            background: 'var(--brand-gold)',
            border: '1.5px solid var(--brand-gold)',
            borderRadius: 6,
            padding: '5px 10px',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--brand-gold-text)',
            cursor: (loading || isTranscribing || !value.trim()) ? 'not-allowed' : 'pointer',
            opacity: (loading || isTranscribing || !value.trim()) ? 0.45 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontFamily: tokens.typography.fontFamily.sans,
          }}
        >
          <Send size={12} strokeWidth={2.2} />
          <span>Send</span>
        </button>
      </div>

      <style>{`
        @keyframes sage-comp-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }
        @keyframes sage-comp-spin { to { transform: rotate(360deg) } }
        .sage-comp-btn:hover:not(:disabled) { background: rgba(148,163,184,0.14) !important }
      `}</style>
    </div>
  )
})

export default SageComposer
